import * as PIXI from "pixi.js";

export class PaintRevealOverlay {
  /**
   * @param {HTMLElement} containerElement - The DOM container to append the canvas to.
   * @param {Object} [options] - Optional configurations.
   * @param {string} [options.overlayColor] - Hex color string for the overlay screen (e.g., '#111111').
   * @param {number} [options.sweepDuration] - Duration of phase 1 sweep in frames (default: 35).
   * @param {number} [options.totalDuration] - Total animation duration in frames (default: 50).
   * @param {number} [options.speedMultiplier] - Path traversal speed scalar (default: 0.28).
   * @param {Function} [options.onComplete] - Callback executed when the animation finishes.
   */
  constructor(containerElement, options = {}) {
    this.container = containerElement;
    this.onComplete = options.onComplete || null;
    this.overlayColor = options.overlayColor || "#111111";

    this.app = null;
    this.maskTexture = null;
    this.brushSprite = null;
    this.cover = null;
    this.maskSprite = null;

    this.BASE_WIDTH = 480;
    this.BASE_HEIGHT = 360;

    // --- TIMING / SPEED CONFIGURATION ---
    // Lower durations = faster execution
    this.sweepDuration = options.sweepDuration ?? 35;
    this.totalDuration = options.totalDuration ?? 50;
    this.speedMultiplier = options.speedMultiplier ?? 0.28;

    this.elapsedFrames = 0;
    this.time = 0;

    this.lastX = window.innerWidth / 2;
    this.lastY = window.innerHeight / 2;
    this.freezeX = 0;
    this.freezeY = 0;

    this._resizeHandler = this._handleResize.bind(this);
    this._updateHandler = this._update.bind(this);

    this._init();
  }

  async _init() {
    this.app = new PIXI.Application();
    await this.app.init({
      width: window.innerWidth,
      height: window.innerHeight,
      backgroundAlpha: 0,
      antialias: true,
    });

    this.container.appendChild(this.app.canvas);
    
    const containerRef = document.getElementById("reveal-container");
    if (containerRef) {
      containerRef.style.opacity = 1;
    }

    const brushCanvas = this._createProceduralBrushCanvas();
    const paintTexture = PIXI.Texture.from(brushCanvas);

    this.brushSprite = new PIXI.Sprite(paintTexture);
    this.brushSprite.anchor.set(0.5);
    this.brushSprite.width = this.BASE_WIDTH;
    this.brushSprite.height = this.BASE_HEIGHT;

    this.maskTexture = PIXI.RenderTexture.create({
      width: this.app.screen.width,
      height: this.app.screen.height,
    });
    this._clearMaskTexture();

    this.cover = new PIXI.Graphics();
    this.cover.rect(0, 0, this.app.screen.width, this.app.screen.height);
    this.cover.fill(this.overlayColor);

    this.maskSprite = new PIXI.Sprite(this.maskTexture);

    this.cover.setMask({
      mask: this.maskSprite,
      channel: "red",
      inverse: true,
    });

    this.app.stage.addChild(this.cover);

    window.addEventListener("resize", this._resizeHandler);
    this.app.ticker.add(this._updateHandler);
  }

  _createProceduralBrushCanvas() {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext("2d");

    const grad = ctx.createLinearGradient(0, 0, 512, 0);
    grad.addColorStop(0, "rgba(255,0,0,0)");
    grad.addColorStop(0.2, "rgba(255,0,0,0.95)");
    grad.addColorStop(0.5, "rgba(255,0,0,1)");
    grad.addColorStop(0.8, "rgba(255,0,0,0.95)");
    grad.addColorStop(1, "rgba(255,0,0,0)");

    ctx.fillStyle = grad;
    ctx.fillRect(0, 100, 512, 312);

    ctx.fillStyle = "rgba(0,0,0,1)";
    ctx.globalCompositeOperation = "destination-out";
    for (let i = 0; i < 450; i++) {
      let x = Math.random() * 512;
      let y = 80 + Math.random() * 350;
      let w = 1 + Math.random() * 3;
      let h = 40 + Math.random() * 160;
      ctx.fillRect(x, y, w, h);
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "rgba(255,0,0,0.03)";
    ctx.filter = "blur(4px)";
    ctx.fillRect(0, 0, 512, 512);

    return canvas;
  }

  _clearMaskTexture() {
    const initialClear = new PIXI.Graphics()
      .rect(0, 0, this.app.screen.width, this.app.screen.height)
      .fill(0x000000);
    this.app.renderer.render({
      target: this.maskTexture,
      container: initialClear,
      clear: true,
    });
    initialClear.destroy();
  }

  _update(ticker) {
    const delta = ticker.deltaTime;
    this.elapsedFrames += delta;

    if (this.elapsedFrames < this.sweepDuration) {
      // --- PHASE 1: Fast Fluid Sweep ---
      this.time += this.speedMultiplier * delta;

      const x =
        this.app.screen.width / 2 +
        Math.sin(this.time * 1.1) * (this.app.screen.width * 0.65);
      const y =
        this.app.screen.height / 2 +
        Math.cos(this.time * 0.45) * (this.app.screen.height * 0.65);

      const dx = x - this.lastX;
      const dy = y - this.lastY;
      if (Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1) {
        this.brushSprite.rotation = Math.atan2(dy, dx) + Math.PI / 2;
      }

      this.brushSprite.position.set(x, y);

      this.freezeX = x;
      this.freezeY = y;
      this.lastX = x;
      this.lastY = y;
    } else {
      // --- PHASE 2: Fast Expansion ---
      this.brushSprite.position.set(this.freezeX, this.freezeY);

      const progress =
        (this.elapsedFrames - this.sweepDuration) /
        (this.totalDuration - this.sweepDuration);
      const easeOutCubic = 1.0 - Math.pow(1.0 - progress, 3);

      const maxDimension = Math.max(
        this.app.screen.width,
        this.app.screen.height,
      );
      const targetScale = (maxDimension * 3.5) / this.BASE_WIDTH;
      const currentScale = 1.0 + (targetScale - 1.0) * easeOutCubic;

      this.brushSprite.width = this.BASE_WIDTH * currentScale;
      this.brushSprite.height = this.BASE_HEIGHT * currentScale;
    }

    this.app.renderer.render({
      target: this.maskTexture,
      container: this.brushSprite,
      clear: false,
    });

    if (this.elapsedFrames >= this.totalDuration) {
      this.destroy();
    }
  }

  _handleResize() {
    if (!this.app || !this.cover) return;
    this.app.renderer.resize(window.innerWidth, window.innerHeight);

    this.cover.clear();
    this.cover.rect(0, 0, this.app.screen.width, this.app.screen.height);
    this.cover.fill(this.overlayColor);

    const nextTexture = PIXI.RenderTexture.create({
      width: this.app.screen.width,
      height: this.app.screen.height,
    });

    this.maskTexture.destroy(true);
    this.maskTexture = nextTexture;
    this._clearMaskTexture();

    if (this.maskSprite) {
      this.maskSprite.texture = this.maskTexture;
    }
  }

  destroy() {
    window.removeEventListener("resize", this._resizeHandler);

    const canvasNode = this.app && this.app.canvas;

    if (this.app) {
      this.app.ticker.remove(this._updateHandler);
      this.app.ticker.stop();

      if (this.maskTexture) {
        this.maskTexture.destroy(true);
      }
      this.app.destroy(true, { children: true, texture: true });
    }

    if (canvasNode && canvasNode.parentNode) {
      canvasNode.remove();
    }

    if (this.onComplete) {
      this.onComplete();
    }
  }
}